// Phase 0089 — Live Study Home.
// Mobile-first list of active studies. Tap a card → opens the start wizard.

import { App, Button, Card, Col, Empty, Row, Space, Tag, Typography } from 'antd'
import { ClockCircleOutlined, PlayCircleFilled, RightOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { liveStudyApi, type StudyMaster } from '@/features/hr/liveStudyApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title } = Typography

const CATEGORY_COLOR: Record<string, string> = {
  WORKFORCE: 'blue',
  IE: 'purple',
  TRAINING: 'cyan',
  HR: 'magenta',
  FATIGUE: 'orange',
  SAFETY: 'red',
  QUALITY: 'green',
  MAINTENANCE: 'volcano',
}

export default function LiveStudyHomePage() {
  const navigate = useNavigate()
  const { message: msg } = App.useApp()

  const { data: studies = [], isLoading } = useQuery({
    queryKey: ['live-studies-active'],
    queryFn: () => liveStudyApi.listStudies({ active_only: true }),
  })

  const { data: dashboard } = useQuery({
    queryKey: ['live-studies-dashboard'],
    queryFn: () => liveStudyApi.dashboard(),
  })

  const { data: recent = [] } = useQuery({
    queryKey: ['live-studies-history-recent'],
    queryFn: () => liveStudyApi.history(undefined, 5),
  })

  return (
    <>
      <PageHeader
        title="Shop-Floor Studies"
        subtitle="Tap a study card to start a live capture session"
        extra={
          <Button onClick={() => navigate('/hr/live-studies/history')}
                  icon={<ClockCircleOutlined />}>
            History
          </Button>
        }
      />

      {/* KPI strip */}
      {dashboard && (
        <Row gutter={8} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Active</Text>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {String((dashboard as Record<string, unknown>).studies_active || 0)}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Templates</Text>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {String((dashboard as Record<string, unknown>).templates_active || 0)}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Open CAPA</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#cf1322' }}>
                {String((dashboard as Record<string, unknown>).open_capa_recommendations || 0)}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Catalogue</Text>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {String((dashboard as Record<string, unknown>).studies_total || 0)}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Recent sessions for quick resume */}
      {recent.length > 0 && (
        <>
          <Title level={5} style={{ marginBottom: 8 }}>Recent sessions</Title>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
            {recent.slice(0, 3).map((s) => (
              <Card
                key={s.id}
                size="small"
                hoverable
                onClick={() => navigate(`/hr/live-studies/sessions/${s.id}`)}
              >
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space direction="vertical" size={2}>
                    <Text strong>{s.session_no}</Text>
                    <Space size={4}>
                      <Tag>{s.session_status}</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {s.study_date}
                      </Text>
                    </Space>
                  </Space>
                  <RightOutlined />
                </Space>
              </Card>
            ))}
          </Space>
        </>
      )}

      <Title level={5} style={{ marginBottom: 8 }}>Studies — tap to start</Title>
      {studies.length === 0 ? (
        <Empty description="No active studies. Activate from Study Master." />
      ) : (
        <Row gutter={[12, 12]}>
          {studies.map((s: StudyMaster) => (
            <Col xs={24} sm={12} md={8} lg={6} key={s.id}>
              <Card
                hoverable
                onClick={() => navigate(`/hr/live-studies/start?study_id=${s.id}`)}
                bodyStyle={{ padding: 16 }}
                style={{ height: '100%' }}
              >
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Tag color={CATEGORY_COLOR[s.study_category] || 'default'}>
                    {s.study_category}
                  </Tag>
                  <Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>
                    {s.study_name}
                  </Text>
                  <Space size={4}>
                    {s.recommended_frequency && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {s.recommended_frequency}
                      </Text>
                    )}
                    {s.priority_rank && (
                      <Tag color="gold" style={{ fontSize: 10 }}>#{s.priority_rank}</Tag>
                    )}
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      block
                      icon={<PlayCircleFilled />}
                      style={{ height: 44, fontSize: 14, fontWeight: 600 }}
                    >
                      Start Study
                    </Button>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </>
  )
}
